import React from 'react';
import { accent } from '../theme/tokens';

/**
 * Hero 装饰层 —— 浅松绿背景上的极淡线性图案。
 * 意象:上升折线(进步)、嫩芽(成长)、星点(闪光点)、同心弧(积累)。
 * 纯 SVG 内联、低透明度、绝对定位铺满,置于内容之下(zIndex 0)。
 *
 * 用法:放在 position:relative 的 Hero 容器内第一个子元素,
 * Hero 内的文字/按钮记得加 position:relative + zIndex>=1。
 */
const HeroPattern: React.FC<{ opacity?: number }> = ({ opacity = 1 }) => {
  const teal = accent[600]; // #0d9488 —— 比背景深一档的松绿

  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
        opacity,
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 500 220"
        preserveAspectRatio="xMaxYMid slice"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* 右上角松绿光晕 */}
        <defs>
          <radialGradient id="tealGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={teal} stopOpacity="0.14" />
            <stop offset="100%" stopColor={teal} stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="470" cy="20" r="180" fill="url(#tealGlow)" />

        <g stroke={teal} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
          {/* 同心弧 —— 积累/年轮 */}
          <path d="M500 200 A 120 120 0 0 0 300 120" strokeOpacity="0.15" />
          <path d="M500 200 A 165 165 0 0 0 280 70" strokeOpacity="0.10" />
          <path d="M500 200 A 210 210 0 0 0 250 30" strokeOpacity="0.07" />

          {/* 上升折线 —— 进步曲线 */}
          <path d="M300 175 L340 150 L375 160 L410 120 L450 128 L485 90" strokeOpacity="0.28" strokeWidth="2" />
          {/* 折线节点 */}
          <circle cx="340" cy="150" r="2.5" fill={teal} fillOpacity="0.45" stroke="none" />
          <circle cx="410" cy="120" r="2.5" fill={teal} fillOpacity="0.45" stroke="none" />
          <circle cx="485" cy="90" r="3" fill={teal} fillOpacity="0.55" stroke="none" />

          {/* 嫩芽 —— 成长 */}
          <path d="M360 200 L360 168" strokeOpacity="0.22" />
          <path d="M360 182 C 348 178 342 166 344 156 C 356 158 362 170 360 182" strokeOpacity="0.22" />
          <path d="M360 174 C 372 170 378 160 376 150 C 366 152 360 162 360 174" strokeOpacity="0.22" />
        </g>

        {/* 星点 —— 闪光点 */}
        <g fill={teal}>
          <Star cx={430} cy={55} r={4} o={0.32} />
          <Star cx={465} cy={150} r={3} o={0.22} />
          <Star cx={315} cy={90} r={2.5} o={0.18} />
        </g>
      </svg>
    </div>
  );
};

// 四角星
const Star: React.FC<{ cx: number; cy: number; r: number; o: number }> = ({ cx, cy, r, o }) => (
  <path
    d={`M${cx} ${cy - r} C ${cx + r * 0.2} ${cy - r * 0.2} ${cx + r * 0.2} ${cy - r * 0.2} ${cx + r} ${cy}
        C ${cx + r * 0.2} ${cy + r * 0.2} ${cx + r * 0.2} ${cy + r * 0.2} ${cx} ${cy + r}
        C ${cx - r * 0.2} ${cy + r * 0.2} ${cx - r * 0.2} ${cy + r * 0.2} ${cx - r} ${cy}
        C ${cx - r * 0.2} ${cy - r * 0.2} ${cx - r * 0.2} ${cy - r * 0.2} ${cx} ${cy - r} Z`}
    fillOpacity={o}
  />
);

export default HeroPattern;
